import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { API } from '../../App';

// Current app version - increment this when deploying new versions
export const APP_VERSION = '1.2.0';

// Local storage key for dismissed version
const DISMISSED_VERSION_KEY = 'outsiders_dismissed_version';
const LAST_CHECK_KEY = 'outsiders_last_version_check';

export default function AppUpdateNotification() {
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const [updateNotes, setUpdateNotes] = useState('');

  useEffect(() => {
    checkForUpdates();
    
    // Check for updates every 5 minutes while app is open
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const checkForUpdates = async () => {
    try {
      // Check if we already dismissed this version recently
      const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY);
      const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
      const now = Date.now();
      
      // Only check once per minute to avoid excessive requests
      if (lastCheck && (now - parseInt(lastCheck)) < 60000) {
        return;
      }
      
      localStorage.setItem(LAST_CHECK_KEY, now.toString());

      const response = await fetch(`${API}/app/version`);
      if (response.ok) {
        const data = await response.json();
        const serverVersion = data.version;
        
        // Compare versions
        if (serverVersion && serverVersion !== APP_VERSION) {
          // Check if user already dismissed this specific version
          if (dismissedVersion !== serverVersion) {
            setLatestVersion(serverVersion);
            setUpdateNotes(data.notes || 'Bug fixes and improvements');
            setShowUpdateDialog(true);
          }
        }
      }
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.log('Version check failed:', error);
    }
  };

  const handleUpdate = () => {
    // Force reload the page to get the latest version
    window.location.reload(true);
  };

  const handleDismiss = () => {
    // Remember that user dismissed this version
    if (latestVersion) {
      localStorage.setItem(DISMISSED_VERSION_KEY, latestVersion);
    }
    setShowUpdateDialog(false);
  };

  if (!showUpdateDialog) return null;

  return (
    <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
      <DialogContent className="sm:max-w-md mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-600">
            <Download className="h-5 w-5" />
            Update Available
          </DialogTitle>
          <DialogDescription>
            A new version of the app is available.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-blue-700 font-medium">Current Version</span>
              <span className="text-sm text-blue-600 font-mono">{APP_VERSION}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-700 font-medium">New Version</span>
              <span className="text-sm text-blue-600 font-mono font-bold">{latestVersion}</span>
            </div>
          </div>

          {updateNotes && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">What's New</p>
              <p className="text-slate-700 text-sm">{updateNotes}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="flex-1 h-12 rounded-xl"
          >
            <X className="h-4 w-4 mr-2" />
            Later
          </Button>
          <Button
            onClick={handleUpdate}
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Update Now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
